import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Alert, 
  Paper, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Divider,
  CircularProgress,
  TextField
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { authenticatedFetch } from '../utils/apiUtils';
// Consolidated import from utils - returns parsed data directly (not Response objects)
import { getSavedFilters, getSavedFilterById, applySavedFilter } from '../utils/savedFiltersUtils';

/**
 * UploadFile component - handles CSV file uploads and initial data parsing
 */
const UploadFile = ({ onNext, setLoading, setFileData, setSelectedColumns, setFilterParams, setFilterConfig, setJobName }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [fileStats, setFileStats] = useState(null);
  const [savedFilters, setSavedFilters] = useState([]);
  const [selectedFilterId, setSelectedFilterId] = useState('');
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [jobNameInput, setJobNameInput] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // Cool loading messages that rotate
  const loadingMessages = [
    "Crunching your data...",
    "Making magic happen ✨",
    "Processing like a boss...",
    "Almost there, hang tight!",
    "Doing the heavy lifting...",
    "Creating something amazing...",
    "Working our magic...",
    "Loading with style...",
    "Making it happen...",
    "Just a sec, we got this!"
  ];

  // Handle file selection with auto-upload
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setError(null);
    setUploadSuccess(false);
    
    if (!selectedFile) {
      return;
    }
    
    if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
      setError('Please upload a CSV file.');
      return;
    }
    
    setFile(selectedFile);
    // Auto-upload immediately when file is selected
    autoUpload(selectedFile);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setError(null);
      setLoading(true);
      
      // Use authenticated fetch instead of axios
      const response = await authenticatedFetch('/api/upload', {
        method: 'POST',
        body: formData,
        // Don't include Content-Type header for FormData, browser will set it with boundary
        headers: {}
      });
      
      if (!response.ok) {
        // Handle error responses
        try {
          const errorData = await response.json();
          // Check if error is an object or string and handle accordingly
          if (typeof errorData.error === 'object') {
            throw new Error(JSON.stringify(errorData.error) || `Upload failed with status: ${response.status}`);
          } else {
            throw new Error(errorData.error || `Upload failed with status: ${response.status}`);
          }
        } catch (jsonError) {
          // If JSON parsing fails, use the status text
          throw new Error(`Upload failed: ${response.statusText || response.status}`);
        }
      }
      
      const responseData = await response.json();
      setLoading(false);
      setUploadSuccess(true);
      
      // Store file data in state for later use
      const { fileId, rowCount, columnCount, columns } = responseData;
      setFileStats({ rowCount, columnCount });
      // Use 'id' instead of 'fileId' to ensure consistent property naming throughout the application
      setFileData({ id: fileId, rowCount, columnCount, columns });
      
      // Store fileId in localStorage for the filter application process
      localStorage.setItem('currentFileId', fileId);
      console.log('Stored fileId in localStorage:', fileId);
      
      // Clean up any old job name persistence to prevent duplicates
      localStorage.removeItem('currentJobName');
    } catch (error) {
      setLoading(false);
      console.error('Upload error:', error);
      
      // Ensure we're displaying a string, not an object
      let errorMessage = 'An error occurred during upload';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        // If error is an object, stringify it for display
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error occurred';
        }
      }
      
      setError(errorMessage);
    }
  };

  // Load saved filters on component mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoadingFilters(true);
        // getSavedFilters from utils returns parsed data directly (array of filters)
        const filters = await getSavedFilters();
        setSavedFilters(filters || []);
      } catch (error) {
        console.error('Error loading saved filters:', error);
      } finally {
        setLoadingFilters(false);
      }
    };

    loadFilters();
  }, []);

  // Rotate loading messages when loading
  useEffect(() => {
    let interval;
    if (setLoading && loadingMessages.length > 0) {
      setLoadingMessage(loadingMessages[0]);
      interval = setInterval(() => {
        setLoadingMessageIndex(prev => {
          const nextIndex = (prev + 1) % loadingMessages.length;
          setLoadingMessage(loadingMessages[nextIndex]);
          return nextIndex;
        });
      }, 2000); // Change message every 2 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [setLoading, loadingMessages]);

  // Handle moving to next step
  const handleNext = () => {
    if (!uploadSuccess) {
      setError('Please upload a file first.');
      return;
    }

    // Validate job name is provided
    if (!jobNameInput.trim()) {
      setError('Please provide a job name before continuing.');
      return;
    }

    // Pass job name to parent component and persist it
    if (setJobName) {
      const trimmedJobName = jobNameInput.trim();
      setJobName(trimmedJobName);
      // Note: No longer persisting job names to prevent duplicate name issues
      console.log('Job name set and persisted:', trimmedJobName);
    }
    
    // Clear all previous state if continuing with manual selection
    // This prevents stale data from affecting the new workflow
    if (savedFilters.length > 0) {
      // Keep the fileData since we need it, but reset all other state
      setSelectedColumns([]);
      setFilterParams({
        model: 'gpt-4o', // Always using gpt-4o as the default model
        reasoningText: '',
        chunkSize: 300
      });
      setFilterConfig({});
      console.log('All previous filter data cleared for fresh start');
    }
    
    onNext();
  };
  
  // Apply a selected saved filter (template) - pre-fill form fields only
  const handleApplySavedFilter = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (!selectedFilterId) {
        setError('Please select a template to apply.');
        setLoading(false);
        return;
      }
      
      // Validate job name is provided 
      if (!jobNameInput.trim()) {
        setError('Please provide a list name before applying a template.');
        setLoading(false);
        return;
      }

      // Validate file upload
      if (!uploadSuccess) {
        setError('Please upload a file first.');
        setLoading(false);
        return;
      }
      
      // Set job name for the user's new job
      if (setJobName) {
        const trimmedJobName = jobNameInput.trim();
        setJobName(trimmedJobName);
        // Note: No longer persisting job names to prevent duplicate name issues
        console.log('[TEMPLATE] Job name set for user job:', trimmedJobName);
      }
      
      console.log('[TEMPLATE] Applying template configuration:', selectedFilterId);
      
      // Get template configuration (no job creation)
      const templateResult = await applySavedFilter(selectedFilterId);
      console.log('[TEMPLATE] Retrieved template configuration:', templateResult);
      
      if (!templateResult.success || !templateResult.config) {
        throw new Error('Failed to retrieve template configuration');
      }
      
      const config = templateResult.config;
      
      // Pre-fill form fields with template configuration
      console.log('[TEMPLATE] Pre-filling form with template data');
      
      // Set selected columns if available
      if (config.selectedColumns && config.selectedColumns.length > 0) {
        setSelectedColumns(config.selectedColumns);
        console.log('[TEMPLATE] Set selected columns:', config.selectedColumns);
      }
      
      // Set filter parameters
      setFilterParams({
        model: config.model || 'gpt-4o',
        reasoningText: config.reasoningText || '',
        chunkSize: config.chunkSize || 300,
        logicMode: config.logicMode || 'AND'
      });
      console.log('[TEMPLATE] Set filter parameters');
      
      // Set column-specific configurations
      if (config.columnConfig && Object.keys(config.columnConfig).length > 0) {
        setFilterConfig(config.columnConfig);
        console.log('[TEMPLATE] Set column configurations');
      } else {
        setFilterConfig({});
      }
      
      // Store template info for potential override application after job completion
      if (config.overrides && Object.keys(config.overrides).length > 0) {
        localStorage.setItem('templateOverrides', JSON.stringify(config.overrides));
        localStorage.setItem('templateId', selectedFilterId);
        console.log('[TEMPLATE] Stored template overrides for post-job application');
      }
      
      setLoading(false);
      
      // Navigate to configure filters step with pre-filled data
      onNext(); // Go to SelectColumns step
      onNext(); // Go to ConfigureFilters step
      
    } catch (error) {
      console.error('[TEMPLATE] Error applying template:', error);
      setError(error.message || 'An error occurred while applying the template');
      setLoading(false);
    }
  };

  const autoUpload = async (droppedFile) => {
    if (!droppedFile) return;
    
    const formData = new FormData();
    formData.append('file', droppedFile);
    
    try {
      setError(null);
      setLoading(true);
      
      const response = await authenticatedFetch('/api/upload', {
        method: 'POST',
        body: formData,
        headers: {}
      });
      
      if (!response.ok) {
        try {
          const errorData = await response.json();
          if (typeof errorData.error === 'object') {
            throw new Error(JSON.stringify(errorData.error) || `Upload failed with status: ${response.status}`);
          } else {
            throw new Error(errorData.error || `Upload failed with status: ${response.status}`);
          }
        } catch (jsonError) {
          throw new Error(`Upload failed: ${response.statusText || response.status}`);
        }
      }
      
      const responseData = await response.json();
      setLoading(false);
      setUploadSuccess(true);
      
      // Store file data in state for later use
      const { fileId, rowCount, columnCount, columns } = responseData;
      setFileStats({ rowCount, columnCount });
      // Use 'id' instead of 'fileId' to ensure consistent property naming throughout the application
      setFileData({ id: fileId, rowCount, columnCount, columns });
      
      // Store fileId in localStorage for the filter application process
      localStorage.setItem('currentFileId', fileId);
      console.log('Stored fileId in localStorage from drag-and-drop:', fileId);
      
      // Clean up any old job name persistence to prevent duplicates
      localStorage.removeItem('currentJobName');
      
      // Set the file state so the UI updates correctly
      setFile(droppedFile);
    } catch (error) {
      setLoading(false);
      console.error('Upload error:', error);
      
      let errorMessage = 'An error occurred during upload';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (typeof error === 'object') {
        try {
          errorMessage = JSON.stringify(error);
        } catch (e) {
          errorMessage = 'Unknown error occurred';
        }
      }
      
      setError(errorMessage);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* 50/50 Layout: Name field + filters on left, drag & drop on right */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {/* Left side: Name field and Apply Filters section */}
        <Box sx={{ 
          flex: 1, 
          backgroundColor: '#161c3e', // Match drag & drop area background
          borderRadius: 2,
          p: 3
        }}>
          {/* Job Name Input Field - Always visible */}
          <Box sx={{ mb: 3 }}>
            <TextField
              label="List Name"
              value={jobNameInput}
              onChange={(e) => setJobNameInput(e.target.value)}
              placeholder="e.g., Enterprise SRE Outreach Q1"
              required
              fullWidth
              variant="outlined"
              helperText="The results file you download will have this name"
              InputLabelProps={{
                sx: {
                  '&.Mui-required .MuiInputLabel-asterisk': {
                    display: 'none'
                  }
                }
              }}
            />
          </Box>

          {/* Apply Saved Filters section - Always visible */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              <BookmarkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Use a Template
            </Typography>

            <FormControl sx={{ width: '100%', mb: 2 }}>
              <InputLabel id="saved-filter-select-label">Select Template</InputLabel>
              <Select
                labelId="saved-filter-select-label"
                value={selectedFilterId}
                onChange={(e) => setSelectedFilterId(e.target.value)}
                label="Select Template"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {savedFilters.map((filter) => (
                  <MenuItem key={filter.id} value={filter.id}>
                    {filter.name} {filter.is_favorite && '⭐'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="secondary"
              onClick={handleApplySavedFilter}
              disabled={!selectedFilterId || !uploadSuccess || !jobNameInput.trim()}
              sx={{
                textTransform: 'none',
                borderRadius: '4px',
                fontWeight: 'medium',
                px: 3,
                width: '100%'
              }}
            >
              Apply Template
            </Button>
          </Box>

          {loadingFilters && (
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <CircularProgress size={24} sx={{ mr: 1 }} />
              <Typography variant="body2">Loading saved filters...</Typography>
            </Box>
          )}
        </Box>

        {/* Right side: Drag & Drop area */}
        <Box sx={{ flex: 1 }}>
          <Box 
            component={Paper} 
            variant="outlined" 
            sx={{ 
              p: 4, 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#161c3e',
              border: `2px dashed ${dragActive ? 'rgba(67, 97, 238, 0.6)' : 'rgba(255, 255, 255, 0.1)'}`,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              minHeight: '300px',
              '&:hover': {
                borderColor: 'rgba(67, 97, 238, 0.4)',
                backgroundColor: '#1a2046'
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }}
            onDragEnter={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setDragActive(false);
              
              if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                const droppedFile = e.dataTransfer.files[0];
                
                if (droppedFile.type !== 'text/csv' && !droppedFile.name.endsWith('.csv')) {
                  setError('Please upload a CSV file.');
                  return;
                }
                
                setFile(droppedFile);
                setError(null);
                setUploadSuccess(false);
                
                autoUpload(droppedFile);
              }
            }}
          >
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="upload-file"
              type="file"
              onChange={handleFileChange}
            />
            
            {!uploadSuccess ? (
              <>
                <CloudUploadIcon sx={{ fontSize: 64, color: dragActive ? 'primary.light' : 'primary.main', mb: 2, transition: 'color 0.2s ease' }} />
                <Typography variant="h6" gutterBottom>
                  {file ? file.name : 'Drag & drop CSV file here'}
                </Typography>
                {file && (
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {`${(file.size / 1024).toFixed(2)} KB`}
                  </Typography>
                )}
                <label htmlFor="upload-file">
                  <Button 
                    variant="outlined" 
                    component="span"
                    sx={{ 
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'text.secondary',
                      textTransform: 'none',
                      borderRadius: '4px'  
                    }}
                  >
                    Browse Files
                  </Button>
                </label>
              </>
            ) : (
              <>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Upload Successful
                </Typography>
                {fileStats && (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {fileStats.rowCount} rows and {fileStats.columnCount} columns detected
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box /> {/* Empty box for spacing */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={!uploadSuccess || !jobNameInput.trim()}
          sx={{
            textTransform: 'none',
            borderRadius: '4px',
            fontWeight: 'medium',
            px: 3
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default UploadFile;
